import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import { MIN_ERP_LEAD_DAYS, startsTooSoonForErp, earliestErpStartDate } from '../../constants/experiments';
import { alternativeGroupLabel, AlternativeOptionRow } from './ReplacementAlternatives';

// The planogram's "picked" green: a product previewed from the lane under the pointer
const PREVIEW = '#15803d';

/**
 * What comes in instead of a product, the same small picker wherever replacements are proposed in one go (Top
 * Productos by machine, the lanes picked on a planogram): the suggestions first — "más margen", whose first one is
 * the recommendation, and "más volumen" — and, typing 2+ letters, any product of the catalogue (`allProducts`) but
 * `excludeId`, the one going out. `previewing`: `value` is only the product under the pointer for now, shown in green.
 */
export const IncomingProductAutocomplete = ({ value, previewing = false, alternatives, allProducts, excludeId, disabled, placeholder, onChange }) => {
  const { t } = useTranslation();
  const options = useMemo(() => [
    ...alternatives,
    ...(allProducts || [])
      .filter((p) => p.id !== excludeId && !alternatives.some((a) => a.id === p.id))
      .map((p) => ({ ...p, group: 'all', key: `all-${p.id}` })),
  ], [alternatives, allProducts, excludeId]);
  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      value={value || null}
      options={options}
      groupBy={(o) => (o.group === 'all' ? t('machines_allProducts') : alternativeGroupLabel(t, o.group))}
      getOptionLabel={(o) => o.name || ''}
      getOptionKey={(o) => o.key ?? o.id}
      // A product can be in both groups: the one it was picked from is the selected one.
      isOptionEqualToValue={(o, v) => o.id === v?.id && (!v.group || o.group === v.group)}
      filterOptions={(opts, state) => {
        if (!state.inputValue || state.inputValue.length < 2) return opts.filter((o) => o.group !== 'all');
        const q = state.inputValue.toLowerCase();
        return opts.filter((o) => o.group !== 'all' || (o.name && o.name.toLowerCase().includes(q))).slice(0, 25);
      }}
      noOptionsText={t('machines_typeToSearch')}
      onChange={(_, v) => onChange(v)}
      renderOption={(props, o) => (
        <li {...props} key={o.key ?? o.id}>
          {o.group === 'all'
            ? <Typography sx={{ fontSize: '12px' }}>{o.name}</Typography>
            : <AlternativeOptionRow option={o} />}
        </li>
      )}
      renderInput={(params) => <TextField {...params} placeholder={placeholder} />}
      sx={{
        '& .MuiInputBase-root': { fontSize: '12px', py: 0 },
        '& .MuiInputBase-input': { py: '2px !important' },
        ...(previewing && {
          '& .MuiInputBase-input': { py: '2px !important', color: PREVIEW, fontWeight: 600 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: `${PREVIEW} !important`, borderWidth: '2px' },
        }),
      }}
    />
  );
};

/** Start date and weeks of the experiments about to be created, with their end and the ERP's lead-time rule. */
export const ExperimentStartFields = ({ startDate, onStartDateChange, weeks, onWeeksChange, endDate, locale }) => {
  const { t } = useTranslation();
  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, pt: 1.25, borderTop: `1px solid ${colors.border.light}` }}>
          <DatePicker
            value={startDate}
            minDate={earliestErpStartDate()}
            onChange={(d) => d && onStartDateChange(d)}
            label={t('productMachines_start')}
            slotProps={{ textField: { size: 'small', sx: { width: 150, '& .MuiPickersInputBase-root, & .MuiOutlinedInput-root': { fontSize: '12px', height: '32px' } } } }}
          />
          <TextField
            type="number"
            size="small"
            label={t('productMachines_weeks')}
            value={weeks}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (n >= 1 && n <= 52) onWeeksChange(n);
            }}
            inputProps={{ min: 1, max: 52 }}
            sx={{ width: 80, '& .MuiOutlinedInput-root': { fontSize: '12px', height: '32px' } }}
          />
          <Typography sx={{ fontSize: '11px', color: colors.text.secondary }}>
            → {endDate.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}
          </Typography>
        </Box>
      </LocalizationProvider>
      {startsTooSoonForErp(startDate) && (
        <Typography sx={{ fontSize: '11px', color: '#d32f2f', fontWeight: 600, mt: 0.5 }}>
          {t('erpLeadTimeRule', { days: MIN_ERP_LEAD_DAYS })}
        </Typography>
      )}
    </>
  );
};
